import { supportedLocales } from 'api-validations/localization'
import { MeasureSystem } from 'api-validations/shared'
import type { TFunctionNonStrict, t } from 'i18next'
import type React from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isNil, isNonNil, type Metric, nameForMetricUnit, toMetricsFormatter } from 'utils'

import { useLocalStorage } from '../../hooks/useLocalStorage'

export enum DateTimeFormat {
  /**
   * e.g., 'Oct 16, 2024'
   */
  LongDate = 'longDate',
  /**
   * e.g., 'Oct 16'
   */
  MonthDay = 'monthDay',
  /**
   * e.g., '10/16'
   */
  ShortDate = 'shortDate',
  /**
   * e.g., '10/16, 2:07 PM'
   * will give relative time if the date is within the last 6 hours (e.g., '2 hours ago')
   */
  LongDateTime = 'longDateTime',
  /**
   * e.g., '10/16, 2:07 PM'
   * will give relative time if the date is within the last 6 hours (e.g., '2 hours ago')
   */
  ShortDateTime = 'shortDateTime',
  /**
   * e.g., '2:07 PM'
   */
  ShortTime = 'shortTime'
}

const fallbackLocale = supportedLocales[0]

const createDateTimeFormatters = (locale: string): Record<DateTimeFormat, Intl.DateTimeFormat> => ({
  longDateTime: new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric'
  }),
  shortDateTime: new Intl.DateTimeFormat(locale, {
    year: undefined,
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  }),
  shortTime: new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: 'numeric'
  }),
  shortDate: new Intl.DateTimeFormat(locale, {
    month: 'numeric',
    day: 'numeric'
  }),
  longDate: new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }),
  monthDay: new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric'
  })
})

export { MeasureSystem }

export type LocalizationContext = {
  locale: string
  setLocale: (locale: string) => void
  formatMetric: (metric: Metric, opts?: { includeUnits: boolean }) => string
  localizeUnit: (unit?: string) => string
  formatDate: (date?: Date, formatType?: DateTimeFormat) => string
  /**
   * Formats the span between two dates as the locale writes one, collapsing the parts they share, so
   * two dates on one day read as that day.
   */
  formatDateRange: (start: Date, end: Date, formatType: DateTimeFormat) => string
  /**
   * Formats a monetary amount, given in the currency's smallest unit (e.g. cents),
   * into a localized currency string. `currency` is an ISO 4217 code (e.g. "USD").
   */
  formatCurrency: (amountInMinorUnits: number, currency: string) => string
  t: typeof t
  /**
   * A version of the t function that allows passing in a path as a string.
   * This is the discouraged old api but is valuable for dynamic translations that
   * use string paths, e.g., an enum like:
   * ```ts
   * enum Color {
   * Red = 'colors.red',
   * Blue = 'colors.blue'
   * }
   * ```
   * and you want to translate based on the enum value:
   * ```ts
   * const colorTranslation = tByPath(colorEnumValue)
   * ```
   */
  tByPath: TFunctionNonStrict
  supportedLocales: readonly string[]
  measureSystem: MeasureSystem
  setMeasureSystem: (measureSystem: MeasureSystem) => void
  formatLocale: (locale: string) => string
}

const LocaleContext = createContext<LocalizationContext | null>(null)

export const BrowserLocalizationProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const bestMatchLocale = navigator.languages
    .map((lang) => {
      const userLocale = new Intl.Locale(lang)
      const exactMatch = supportedLocales.find(
        (locale) => locale.toString() === userLocale.toString()
      )
      return (
        exactMatch ??
        supportedLocales.find((locale) => new Intl.Locale(locale).language === userLocale.language)
      )
    })
    .find(isNonNil)

  const [savedLocale, setLocale] = useLocalStorage<string | undefined>({
    defaultValue: bestMatchLocale,
    key: 'locale'
  })

  const locale = new Intl.Locale(savedLocale ?? fallbackLocale).toString()

  const [savedMeasureSystem, setMeasureSystem] = useLocalStorage({
    defaultValue: locale === 'en-US' ? MeasureSystem.Imperial : MeasureSystem.Metric,
    key: 'measureSystem'
  })

  useEffect(() => {
    // Chrome offers to translate a page whose declared language disagrees with its text, and a
    // translated page breaks React's DOM updates, so the document must follow the active locale.
    document.documentElement.lang = locale
  }, [locale])

  return (
    <LocalizationProvider
      locale={locale}
      setLocale={setLocale}
      measureSystem={savedMeasureSystem}
      setMeasureSystem={setMeasureSystem}
    >
      {children}
    </LocalizationProvider>
  )
}

type LocalizationProviderProps = {
  children: React.ReactNode
  locale: string
  setLocale?: (locale: string) => void
  measureSystem: MeasureSystem
  setMeasureSystem?: (measureSystem: MeasureSystem) => void
}

const ONE_MINUTE = 60 * 1000
const ONE_HOUR = 60 * ONE_MINUTE

export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({
  children,
  locale,
  setLocale = () => {},
  measureSystem,
  setMeasureSystem = () => {}
}) => {
  const { t, i18n } = useTranslation(undefined, { lng: locale })

  useEffect(() => {
    if (locale !== i18n.language) {
      i18n.changeLanguage(locale)
    }
  }, [locale, i18n])

  const isMetricSystem = measureSystem === MeasureSystem.Metric

  const localizeUnit = useCallback(
    (unit = '') =>
      t(($) => $.units[isMetricSystem ? (nameForMetricUnit(unit) as never) : (unit as never)], {
        defaultValue: unit,
        count: 1
      }),
    [isMetricSystem, t]
  )

  const formatMetric = useCallback(
    (metric: Metric, opts = { includeUnits: true }) => {
      const formattedMetric = toMetricsFormatter({ locale, isMetricSystem })(metric)
      const untranslatedUnit = formattedMetric[1]
      if (opts.includeUnits && isNonNil(untranslatedUnit)) {
        formattedMetric[1] = t(($) => $.units[untranslatedUnit as never], {
          defaultValue: untranslatedUnit,
          count: 1
        })
      }
      return opts.includeUnits ? formattedMetric.join(' ') : formattedMetric[0]
    },
    [locale, isMetricSystem, t]
  )

  const formatCurrency = useCallback(
    (amountInMinorUnits: number, currency: string) => {
      const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency.toUpperCase()
      })
      // Scale from the currency's smallest unit by its own fraction-digit count so
      // zero-decimal currencies (e.g. JPY) and 2-decimal ones (e.g. USD) both work.
      const minorUnitDigits = formatter.resolvedOptions().maximumFractionDigits ?? 2
      return formatter.format(amountInMinorUnits / 10 ** minorUnitDigits)
    },
    [locale]
  )

  const formatDate = useMemo(() => {
    const formatters = createDateTimeFormatters(locale)
    const relativeTimeFormatter = new Intl.RelativeTimeFormat(locale, {
      numeric: 'auto',
      style: 'short'
    })

    return (date?: Date, formatType = DateTimeFormat.ShortDateTime) => {
      if (!date) {
        return '--'
      }

      if (
        formatType !== DateTimeFormat.ShortDateTime &&
        formatType !== DateTimeFormat.LongDateTime
      ) {
        return formatters[formatType].format(date)
      }

      const now = Date.now()
      const diff = now - date.getTime()

      switch (true) {
        case diff < ONE_MINUTE:
          return relativeTimeFormatter.format(-Math.floor(diff / 1000), 'second')
        case diff < ONE_HOUR:
          return relativeTimeFormatter.format(-Math.floor(diff / ONE_MINUTE), 'minute')
        case diff < 6 * ONE_HOUR:
          return relativeTimeFormatter.format(-Math.floor(diff / ONE_HOUR), 'hour')
        default:
          return formatters[formatType].format(date)
      }
    }
  }, [locale])

  const formatDateRange = useMemo(() => {
    const formatters = createDateTimeFormatters(locale)
    return (start: Date, end: Date, formatType: DateTimeFormat) =>
      formatters[formatType].formatRange(start, end)
  }, [locale])

  const value = useMemo(
    () => ({
      locale,
      tByPath: t as TFunctionNonStrict,
      setLocale,
      formatMetric,
      localizeUnit,
      /**
       * Defaults to DateTimeFormat.ShortDateTime
       */
      formatDate,
      formatDateRange,
      formatCurrency,
      t,
      supportedLocales,
      measureSystem,
      setMeasureSystem,
      formatLocale: (locale: string) =>
        new Intl.DisplayNames([locale], { type: 'language' }).of(locale) ?? locale
    }),
    [
      locale,
      formatMetric,
      localizeUnit,
      formatDate,
      formatDateRange,
      formatCurrency,
      t,
      setLocale,
      measureSystem,
      setMeasureSystem
    ]
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export const useLocalization = () => {
  const context = useContext(LocaleContext)

  if (isNil(context)) {
    throw new Error('useLocalization must be used within a LocalizationProvider')
  }
  return context
}
